/**
 * Created by piotr@revisual on 04/08/2025.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AboutUserStep from "./AboutUserStep";
import { Link, useHistory } from "react-router-dom";
import appConfig from "../../../../appConfig";
import OnboardingProgress from "./OnboardingProgress";
import HowDidYouHearAboutUs from "./HowDidYouHearAboutUs";
import classNames from "classnames";
import ActivateTrialStep from "./ActivateTrialStep";
import AddWebsiteStep from "./AddWebsiteStep";
import AddMembersStep from "./AddMembersStep";
import SelectIntegrationStep from "./SelectIntegrationStep";
import {
  validateDomain,
  validateSubdomain,
} from "../../modals/Domain/DomainForm";
import AxiosConfig from "../../../../AxiosConfig";
import { useUserStore } from "../../../../hooks/redux/user";
import LoadingIndicator from "../../component/LoadingIndicator";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import ApiError from "../../component/ApiError";
import { useAppStore } from "../../../../hooks/redux/app";
import { useSubscriptionPlansStore } from "../../../../hooks/redux/subscriptionPlans";
import useWizardHook from "./useWizardHook";
import {
  hasOnboardingCompleted,
  hasSubscription,
} from "../../helpers/organization";
import useVisibilityHook from "../../../../hooks/useVisibilityHook";
import { rehydrateFromStorage } from "../../store";
import { useDomainsStore } from "../../../../hooks/redux/domains";
import { useOrganizationMembersStore } from "../../../../hooks/redux/organizationMembers";
import UnauthorizedUserMobileHelp from "../../component/UnauthorizedUserMobileHelp";
import useNotificationToast from "../../hooks/useNotificationToastHook";
import NotificationPopup from "../../component/NotificationPopup";
import { formatError } from "../../../../helpers/errors";
import { FreePlanPrice } from "../../consts/pricing";
import { getReferralId } from "../../../../actions/subscription";

const valueOrOther = (value, otherValue) => {
  if (value && value.length && value.localeCompare("Other")) {
    return value;
  }

  if (otherValue.length) {
    return otherValue;
  }

  return "Other";
};

const Steps = {
  aboutUser: "aboutUser",
  whereDidYouHearFromUs: "whereDidYouHearFromUs",
  activateTrial: "activateTrial",
  addMembers: "addMembers",
  addWebsite: "addWebsite",
  selectIntegration: "selectIntegration",
};

const StepForms = {
  [Steps.aboutUser]: AboutUserStep,
  [Steps.whereDidYouHearFromUs]: HowDidYouHearAboutUs,
  [Steps.activateTrial]: ActivateTrialStep,
  [Steps.addMembers]: AddMembersStep,
  [Steps.addWebsite]: AddWebsiteStep,
  [Steps.selectIntegration]: SelectIntegrationStep,
};

const Validators = {
  [Steps.aboutUser]: (form) => {
    const errors = {};
    if (!form.industry.length) {
      errors.industry = "Industry is required.";
    }

    if (!form.role.length) {
      errors.role = "Role is required.";
    }
    return errors;
  },
  [Steps.whereDidYouHearFromUs]: (form) => {
    const errors = {};
    if (!form.howDidYouHear.length) {
      errors.howDidYouHear = "Please select how you heard about us.";
    }

    return errors;
  },
  [Steps.activateTrial]: (form) => {
    const errors = {};

    // Add any validation logic for the activate trial step if needed
    return errors;
  },
  [Steps.addMembers]: (form) => {
    const errors = {};
    if (!form.emails.length) {
      errors.emails = "Please add at least one email address.";
    }

    return errors;
  },
  [Steps.addWebsite]: (form) => {
    const errors = {};

    if (!validateDomain(form.websiteDomain)) {
      errors.websiteDomain = "Please enter a correct website domain name";

      const parser = document.createElement("a");
      parser.href = form.websiteDomain;

      if (form.websiteDomain.match(/^(https?)\:\/\//)) {
        errors.websiteDomain +=
          ": Remove the protocol from the domain name (http:// or https://)";
      } else if (form.websiteDomain.match(/(\:\d)?(\/).(.+)/)) {
        const path = form.websiteDomain.match(/(\:\d)?(\/).(.+)/);
        errors.websiteDomain +=
          ": Remove the website path from the domain name (" + path[0] + ")";
      }
    }

    if (!validateSubdomain(form.websiteDomain)) {
      errors.websiteDomain = (
        <span>
          Remove <strong>www</strong> subdomain from the domain name. It will be
          added automatically. Provide only domain or subdomain name.
        </span>
      );
    }

    return errors;
  },
  [Steps.selectIntegration]: (form) => {
    const errors = {};
    if (!form.integration) {
      errors.integration = "Please select an integration.";
    }

    return errors;
  },
};

const initialFormState = {
  industry: "",
  industryOther: "",
  role: "",
  roleOther: "",
  howDidYouHear: "",
  howDidYouHearOther: "",
  emails: [],
  websiteDomain: "",
  integration: "",
  plan: null,
};

const stepFields = {
  [Steps.aboutUser]: ["industry", "industryOther", "role", "roleOther"],
  [Steps.whereDidYouHearFromUs]: ["howDidYouHear", "howDidYouHearOther"],
  [Steps.activateTrial]: [],
  [Steps.addMembers]: ["emails"],
  [Steps.addWebsite]: ["websiteDomain"],
  [Steps.selectIntegration]: ["integration"],
};

export const OnboardingPage = () => {
  const [form, setForm] = useState({ ...initialFormState });
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false);

  const history = useHistory();
  const { user, checkSession, logout } = useUserStore();
  const { activateTrial, activateFreePlan, currentOrganization } =
    useCurrentOrganizationStore();
  const { clearErrors } = useAppStore();
  const { fetchPlans, subscriptionPlans } = useSubscriptionPlansStore();
  const { domains } = useDomainsStore();
  const { organizationMembers } = useOrganizationMembersStore();
  const { addNotification } = useNotificationToast();

  const subscription = hasSubscription(currentOrganization);
  const [currentStep, setCurrentStep] = useState(
    subscription ? Steps.addMembers : Steps.aboutUser
  );

  const [finishWizard, startProcessing, pollSession] = useWizardHook(form);
  const formDisabled =
    currentOrganization.activateTrial ||
    currentOrganization.activateFreePlan ||
    subscriptionPlans.fetch ||
    startProcessing ||
    pollSession;

  useEffect(() => {
    if (subscription && hasOnboardingCompleted(currentOrganization)) {
      history.push("/dashboard");
    }
    if (subscription && currentStep === Steps.aboutUser) {
      setCurrentStep(Steps.addMembers);
    }
  }, [subscription, currentStep, currentOrganization, history]);

  const CurrentStepComponent = StepForms[currentStep] || null;

  useEffect(() => {
    clearErrors();
    fetchPlans();
  }, []);

  useVisibilityHook(() => {
    if (!user.model?.is_account_verified) {
      rehydrateFromStorage();
    }
  }, [user.model, checkSession]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const token = params.get("token");

    if (token && token !== "" && !user.checkSession) {
      AxiosConfig.setAuthToken(token);
      checkSession();
    } else {
      setShowForm(true);
    }
    history.replace("/onboarding");
  }, []);

  useEffect(() => {
    if (user.checkSessionSuccess) {
      setShowForm(true);
    }
  }, [user.checkSessionSuccess]);

  // --------------
  useEffect(() => {
    if (
      currentOrganization.activateTrialSuccess &&
      currentStep === Steps.activateTrial
    ) {
      setCurrentStep(Steps.addMembers);
      window.postMessage({ type: "resetConfetti" });
    }
  }, [currentOrganization.activateTrialSuccess, currentStep]);

  useEffect(() => {
    if (
      currentOrganization.activateFreePlanSuccess &&
      currentStep === Steps.activateTrial
    ) {
      setCurrentStep(Steps.addMembers);
    }
  }, [currentOrganization.activateFreePlanSuccess, currentStep]);

  useEffect(() => {
    if (
      form.plan &&
      currentStep === Steps.activateTrial &&
      !currentOrganization.activateTrialSuccess &&
      !currentOrganization.activateFreePlanSuccess
    ) {
      const payload = {
        role: valueOrOther(form.role, form.roleOther),
        industry: valueOrOther(form.industry, form.industryOther),
        source: valueOrOther(form.howDidYouHear, form.howDidYouHearOther),
        referral: getReferralId(),
      };

      if (form.plan === FreePlanPrice.id) {
        // console.log("activating free plan", payload);
        activateFreePlan(payload);
      } else {
        payload.stripe_price_id = form.plan;
        // activate trial here
        activateTrial(payload);
      }
    }
  }, [
    currentOrganization.activateTrialSuccess,
    currentOrganization.activateFreePlanSuccess,
    currentStep,
    form,
  ]);

  useEffect(() => {
    if (currentOrganization.activateTrialError) {
      setForm({
        ...form,
        plan: null,
      });

      const onClose = () => {
        clearErrors();
      };

      addNotification({
        content: (props) => (
          <NotificationPopup onClose={onClose}>
            <div className={"w-100"}>
              <p className="notification-title">
                <strong>The following error occurred:</strong>
              </p>
              <p>{formatError(currentOrganization.activateTrialError)}</p>
            </div>
          </NotificationPopup>
        ),
        title: "The following error occured:",
        type: "danger",
        insert: "top",
        container: "top-center",
        animationIn: ["animated", "fadeIn"],
        animationOut: ["animated", "fadeOut"],
        dismiss: {
          duration: 5000,
          onScreen: true,
        },
      });
    }
  }, [currentOrganization.activateTrialError, addNotification]);

  // ----------------------

  const onFormFieldChange = useCallback(
    (field) => (e) => {
      let value = undefined;

      if (e?.target) {
        value = e.target.value;
      } else {
        value = e;
      }

      setForm((prevForm) => ({
        ...prevForm,
        [field]: value,
      }));
    },
    [setForm]
  );

  const onNextStep = useCallback(
    (direction) => (e) => {
      const currentStepIndex = Object.values(Steps).indexOf(currentStep);

      if (direction === 1) {
        const validateErrors = Validators[currentStep](form);

        if (Object.keys(validateErrors).length > 0) {
          setErrors(validateErrors);
          return;
        } else {
          setErrors({});
        }

        if (currentStep === Steps.selectIntegration) {
          finishWizard();
          return;
        }

        const nextStep = Object.keys(Steps)[currentStepIndex + direction];
        setCurrentStep(nextStep);
      } else if (direction === -1) {
        const previousStep = Object.keys(Steps)[currentStepIndex + direction];
        setCurrentStep(previousStep);
      }
    },
    [currentStep, form]
  );

  const onSkip = useCallback(
    (e) => {
      const currentStepIndex = Object.values(Steps).indexOf(currentStep);

      const nextStep = Object.keys(Steps)[currentStepIndex + 1];
      setCurrentStep(nextStep);

      setErrors({});
      setForm((prevForm) => {
        const updatedForm = { ...prevForm };
        stepFields[currentStep].forEach((field) => {
          updatedForm[field] = initialFormState[field]; // Reset the fields for the skipped step
        });
        return updatedForm;
      });
      e.preventDefault();
    },
    [currentStep, form]
  );

  /**
   *
   * @type {(function(): Promise<void>)|*}
   */
  const onLogout = useCallback(async () => {
    await logout();
    window.location = "/login";
  }, []);

  const progress = useMemo(() => {
    const stepValue = Object.values(Steps).indexOf(currentStep) + 1;
    return (stepValue / Object.keys(Steps).length) * 100;
  }, [currentStep]);

  const nextButtonLabel = useMemo(() => {
    if (currentStep === Steps.activateTrial) {
      return null;
    } else if (currentStep === Steps.selectIntegration) {
      return "Finish";
    }

    return "Next";
  }, [currentStep]);

  const onboardingForm = showForm ? (
    <CurrentStepComponent
      setFormField={onFormFieldChange}
      form={form}
      errors={errors}
    />
  ) : null;

  const footerControls = showForm ? (
    <div>
      <div className="d-flex align-items-center justify-content-between justify-end-one-child">
        <button
          className={classNames("btn btn-outline-primary", {
            "d-none":
              currentStep === Steps.aboutUser ||
              currentStep === Steps.addMembers,
          })}
          onClick={onNextStep(-1)}
          disabled={formDisabled}
        >
          Previous
        </button>
        {nextButtonLabel ? (
          <button
            className="btn btn-primary margin-left-auto"
            onClick={onNextStep(1)}
            disabled={formDisabled}
          >
            {nextButtonLabel}
          </button>
        ) : null}
      </div>
      <div
        className={classNames(
          "align-items-center justify-content-center mt-5",
          {
            "d-none": ![Steps.addMembers, Steps.addWebsite].includes(
              currentStep
            ),
            "d-flex": [Steps.addMembers, Steps.addWebsite].includes(
              currentStep
            ),
          }
        )}
      >
        <button
          className={"btn btn-link"}
          onClick={onSkip}
          disabled={formDisabled}
        >
          Skip for now
        </button>
      </div>
    </div>
  ) : null;

  const progressBar =
    showForm &&
    !formDisabled &&
    !subscriptionPlans.fetch &&
    !startProcessing &&
    !domains.update &&
    !organizationMembers.add &&
    !currentOrganization.updateSetting &&
    !user.checkSession &&
    !pollSession ? (
      <OnboardingProgress progress={progress} />
    ) : (
      <LoadingIndicator />
    );

  return (
    <div className="main-wrapper">
      <div className="account-page">
        <div className={classNames("container container-onboarding")}>
          <div className="account-logo">
            <Link to="/">
              <img
                src={`/img/branding/${appConfig.branding}/full_length_logo.png`}
                alt={appConfig.displayName}
              />
            </Link>
          </div>

          <div className="card-box">
            {progressBar}
            <div className="account-wrapper">
              {onboardingForm}
              {footerControls}
              <ApiError
                error={currentOrganization.activateTrialError}
                className={"m-t-5"}
              />
            </div>
          </div>
          <UnauthorizedUserMobileHelp />
          <button className={"btn btn-link"} onClick={onLogout}>
            ← Start over
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
